import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { tr } from 'date-fns/locale'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

function parseIso(value?: string): Date | undefined {
  if (!value) return undefined
  try {
    const d = parseISO(value)
    return Number.isNaN(d.getTime()) ? undefined : d
  } catch {
    return undefined
  }
}

function toIso(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

export type DateFieldProps = {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  min?: string
  max?: string
  clearable?: boolean
  className?: string
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
}

export function DateField({
  value = '',
  onChange,
  placeholder,
  disabled,
  min,
  max,
  clearable = true,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: DateFieldProps) {
  const { t } = useTranslation()
  const selected = parseIso(value)
  const minDate = parseIso(min)
  const maxDate = parseIso(max)
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState<Date>(() => selected ?? new Date())

  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 })
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })
    const days = eachDayOfInterval({ start, end })
    const rows: Date[][] = []
    for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7))
    return rows
  }, [cursor])

  const weekdays = useMemo(() => {
    const base = startOfWeek(new Date(), { weekStartsOn: 1 })
    return Array.from({ length: 7 }, (_, i) =>
      format(new Date(base.getFullYear(), base.getMonth(), base.getDate() + i), 'EEEEEE', { locale: tr }),
    )
  }, [])

  const label = selected
    ? format(selected, 'd MMMM yyyy', { locale: tr })
    : (placeholder ?? t('common.datePlaceholder'))

  function isDisabledDay(day: Date) {
    if (minDate && isBefore(day, minDate)) return true
    if (maxDate && isAfter(day, maxDate)) return true
    return false
  }

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (disabled) return
        setOpen(v)
        if (v) setCursor(selected ?? new Date())
      }}
    >
      <div
        className={cn(
          'relative flex h-9 w-full items-center rounded-md border border-input bg-card shadow-xs transition-colors',
          'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40',
          ariaInvalid && 'border-destructive ring-destructive/30',
          disabled && 'opacity-50',
          className,
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid || undefined}
            className={cn(
              'inline-flex h-full min-w-0 flex-1 items-center gap-2 rounded-md bg-transparent px-3 text-left text-sm outline-none',
              'hover:bg-muted/30',
              'disabled:cursor-not-allowed',
              !selected && 'text-muted-foreground',
            )}
          >
            <CalendarDays className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate font-medium tabular-nums">{label}</span>
          </button>
        </PopoverTrigger>
        {clearable && selected && !disabled ? (
          <button
            type="button"
            className="mr-1.5 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => onChange?.('')}
            aria-label={t('common.clear')}
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
      <PopoverContent align="start" className="w-[288px] p-0">
        <div className="border-b border-border/70 px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 rounded-lg"
              onClick={() => setCursor((c) => subMonths(c, 1))}
              aria-label={t('common.prevMonth')}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <div className="text-sm font-semibold tracking-tight capitalize">
              {format(cursor, 'LLLL yyyy', { locale: tr })}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 rounded-lg"
              onClick={() => setCursor((c) => addMonths(c, 1))}
              aria-label={t('common.nextMonth')}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>

        <div className="p-3">
          <div className="mb-2 grid grid-cols-7 gap-1">
            {weekdays.map((d) => (
              <div key={d} className="py-1 text-center text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weeks.flat().map((day) => {
              const inMonth = isSameMonth(day, cursor)
              const active = selected ? isSameDay(day, selected) : false
              const today = isToday(day)
              const dayDisabled = isDisabledDay(day)
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  disabled={dayDisabled}
                  onClick={() => {
                    onChange?.(toIso(day))
                    setOpen(false)
                  }}
                  className={cn(
                    'relative flex size-8 items-center justify-center rounded-lg text-sm tabular-nums transition-colors',
                    !inMonth && 'text-muted-foreground/40',
                    inMonth && !active && 'text-foreground hover:bg-muted',
                    today && !active && 'ring-1 ring-border',
                    active && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                    dayDisabled && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                  )}
                >
                  {format(day, 'd')}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border/70 px-3 py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-2 text-xs"
            onClick={() => {
              const today = new Date()
              if (isDisabledDay(today)) return
              onChange?.(toIso(today))
              setCursor(today)
              setOpen(false)
            }}
          >
            {t('common.today')}
          </Button>
          {clearable ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg px-2 text-xs text-muted-foreground"
              onClick={() => {
                onChange?.('')
                setOpen(false)
              }}
            >
              {t('common.clear')}
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
